import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Globe,
  Zap,
  BarChart,
  Smartphone,
  Search,
  CheckCircle,
  ArrowRight,
  MessageCircle,
  Palette,
  Code,
  Gauge,
  MousePointer,
  Eye,
  Settings,
  Layers,
  Target,
  FileText,
  Clock,
  ExternalLink,
} from 'lucide-react';
import { images, contactByLanguage } from '../data/mock';

// Google Colors
const googleColors = {
  blue: '#4285F4',
  red: '#EA4335',
  yellow: '#FBBC04',
  green: '#34A853',
};

const WebCreationPage = () => {
  const { language, t } = useLanguage();

  const currentContact = contactByLanguage[language] || contactByLanguage.es;

  const openWhatsApp = () => {
    window.open(`https://wa.me/${currentContact.whatsapp}`, '_blank');
  };

  const content = {
    es: {
      hero: {
        badge: 'Desarrollo Web Orientado a Conversión',
        title: 'Sitios Web y Landing Pages que Convierten',
        subtitle: 'Diseñamos y desarrollamos sitios web rápidos, modernos y optimizados para convertir visitantes en clientes. Cada elemento está pensado para maximizar tus conversiones.',
      },
      features: {
        title: '¿Por Qué Elegirnos para tu Web?',
        subtitle: 'Desarrollo web con enfoque en resultados medibles',
        items: [
          { icon: <Palette className="w-6 h-6" />, title: 'Diseño CRO', desc: 'Cada elemento diseñado para maximizar conversiones y guiar al usuario hacia la acción.', color: googleColors.blue },
          { icon: <Gauge className="w-6 h-6" />, title: 'Velocidad Optimizada', desc: 'Sitios que cargan en menos de 3 segundos para mejor experiencia y SEO.', color: googleColors.green },
          { icon: <Smartphone className="w-6 h-6" />, title: 'Mobile-First', desc: 'Diseño responsive perfecto en móvil, tablet y desktop.', color: googleColors.red },
          { icon: <BarChart className="w-6 h-6" />, title: 'Tracking Integrado', desc: 'Google Analytics, Tag Manager y seguimiento de conversiones desde el día 1.', color: googleColors.yellow },
          { icon: <Search className="w-6 h-6" />, title: 'SEO Técnico', desc: 'Estructura optimizada para buscadores con schema markup y meta tags.', color: googleColors.blue },
          { icon: <Code className="w-6 h-6" />, title: 'Código Limpio', desc: 'Desarrollo con tecnologías modernas y mejores prácticas.', color: googleColors.green },
        ],
      },
      types: {
        title: 'Tipos de Proyectos Web',
        subtitle: 'Soluciones adaptadas a cada necesidad',
        items: [
          { 
            title: 'Landing Pages', 
            desc: 'Páginas de aterrizaje de alta conversión diseñadas específicamente para campañas Google Ads. Optimizadas para generar leads y maximizar el ROI de tus campañas.',
            features: ['Diseño persuasivo', 'Formularios optimizados', 'A/B testing ready', 'Velocidad <2s', 'Tracking completo', 'Mobile-first'],
            color: googleColors.blue,
            icon: <Target className="w-8 h-8" />,
          },
          { 
            title: 'Sitios Corporativos', 
            desc: 'Presencia online profesional para tu empresa con todas las secciones necesarias: servicios, sobre nosotros, blog y contacto.',
            features: ['Múltiples páginas', 'Blog integrado', 'CMS fácil de usar', 'SEO optimizado', 'Formularios de contacto', 'Responsive design'],
            color: googleColors.green,
            icon: <Globe className="w-8 h-8" />,
          },
          { 
            title: 'Microsites', 
            desc: 'Sitios web específicos para productos, servicios o campañas puntuales. Perfectos para lanzamientos o promociones temporales.',
            features: ['Enfoque en producto', 'Diseño impactante', 'Integración CRM', 'Analytics avanzado', 'Hosting incluido', 'Mantenimiento'],
            color: googleColors.red,
            icon: <Layers className="w-8 h-8" />,
          },
        ],
      },
      process: {
        title: 'Nuestro Proceso de Desarrollo',
        subtitle: 'De la idea al lanzamiento en 2-4 semanas',
        steps: [
          { 
            num: '01', 
            title: 'Briefing & Estrategia', 
            desc: 'Entendemos tus objetivos, público objetivo y necesidades. Definimos la estrategia de conversión.',
            duration: '1-2 días',
            icon: <FileText className="w-6 h-6" />,
          },
          { 
            num: '02', 
            title: 'Diseño UX/UI', 
            desc: 'Creamos wireframes y diseños visuales enfocados en la experiencia de usuario y conversión.',
            duration: '3-5 días',
            icon: <Palette className="w-6 h-6" />,
          },
          { 
            num: '03', 
            title: 'Desarrollo', 
            desc: 'Construimos tu sitio con tecnologías modernas, optimizado para velocidad y SEO.',
            duration: '5-10 días',
            icon: <Code className="w-6 h-6" />,
          },
          { 
            num: '04', 
            title: 'Lanzamiento', 
            desc: 'Publicamos, configuramos tracking y optimizamos rendimiento. Soporte post-lanzamiento incluido.',
            duration: '1-2 días',
            icon: <Zap className="w-6 h-6" />,
          },
        ],
      },
      included: {
        title: '¿Qué Incluye Nuestro Servicio?',
        items: [
          { text: 'Diseño personalizado (no templates)', icon: <Palette className="w-5 h-5" /> },
          { text: 'Desarrollo responsive', icon: <Smartphone className="w-5 h-5" /> },
          { text: 'Optimización de velocidad', icon: <Gauge className="w-5 h-5" /> },
          { text: 'SEO técnico básico', icon: <Search className="w-5 h-5" /> },
          { text: 'Google Analytics 4 integrado', icon: <BarChart className="w-5 h-5" /> },
          { text: 'Google Tag Manager configurado', icon: <Settings className="w-5 h-5" /> },
          { text: 'Formularios optimizados', icon: <FileText className="w-5 h-5" /> },
          { text: 'Certificado SSL', icon: <CheckCircle className="w-5 h-5" /> },
          { text: '30 días de soporte post-lanzamiento', icon: <Clock className="w-5 h-5" /> },
          { text: 'Documentación de uso', icon: <FileText className="w-5 h-5" /> },
        ],
      },
      stats: {
        items: [
          { value: '2-4', label: 'semanas de entrega' },
          { value: '<3s', label: 'tiempo de carga' },
          { value: '100%', label: 'responsive' },
          { value: '30', label: 'días soporte' },
        ],
      },
      portfolio: {
        title: 'Ejemplos de Nuestro Trabajo',
        subtitle: 'Sitios web que hemos creado para nuestros clientes',
        items: [
          { name: 'Arrancaya Barcelona', url: 'https://arrancaya-bcn.es/', desc: 'Servicio de arranque de coches en Barcelona', image: '/images/portfolio-arrancaya.png' },
          { name: 'Mon Certificat PEB', url: 'https://mon-certificat-peb.be/', desc: 'Certificación energética en Bélgica', image: '/images/portfolio-peb.png' },
          { name: 'Vivre Moins Cher', url: 'https://vivre-moins-cher.fr/', desc: 'Comparador de ofertas en Francia', image: '/images/portfolio-vivre.png' },
        ],
      },
    },
    fr: {
      hero: {
        badge: 'Développement Web Orienté Conversion',
        title: 'Sites Web et Landing Pages qui Convertissent',
        subtitle: 'Nous concevons et développons des sites web rapides, modernes et optimisés pour convertir les visiteurs en clients.',
      },
      features: {
        title: 'Pourquoi Nous Choisir pour votre Web ?',
        subtitle: 'Développement web axé sur les résultats mesurables',
        items: [
          { icon: <Palette className="w-6 h-6" />, title: 'Design CRO', desc: "Chaque élément conçu pour maximiser les conversions.", color: googleColors.blue },
          { icon: <Gauge className="w-6 h-6" />, title: 'Vitesse Optimisée', desc: 'Sites qui chargent en moins de 3 secondes.', color: googleColors.green },
          { icon: <Smartphone className="w-6 h-6" />, title: 'Mobile-First', desc: 'Design responsive parfait sur tous les appareils.', color: googleColors.red },
          { icon: <BarChart className="w-6 h-6" />, title: 'Tracking Intégré', desc: 'Google Analytics, Tag Manager dès le jour 1.', color: googleColors.yellow },
          { icon: <Search className="w-6 h-6" />, title: 'SEO Technique', desc: 'Structure optimisée pour les moteurs de recherche.', color: googleColors.blue },
          { icon: <Code className="w-6 h-6" />, title: 'Code Propre', desc: 'Développement avec technologies modernes.', color: googleColors.green },
        ],
      },
      types: {
        title: 'Types de Projets Web',
        subtitle: 'Solutions adaptées à chaque besoin',
        items: [
          { title: 'Landing Pages', desc: "Pages d'atterrissage haute conversion pour campagnes Google Ads.", features: ['Design persuasif', 'Formulaires optimisés', 'A/B testing', 'Vitesse <2s', 'Tracking complet', 'Mobile-first'], color: googleColors.blue, icon: <Target className="w-8 h-8" /> },
          { title: 'Sites Corporate', desc: 'Présence en ligne professionnelle avec toutes les sections nécessaires.', features: ['Pages multiples', 'Blog intégré', 'CMS facile', 'SEO optimisé', 'Formulaires contact', 'Responsive'], color: googleColors.green, icon: <Globe className="w-8 h-8" /> },
          { title: 'Microsites', desc: 'Sites spécifiques pour produits ou campagnes ponctuelles.', features: ['Focus produit', 'Design impactant', 'Intégration CRM', 'Analytics avancé', 'Hosting inclus', 'Maintenance'], color: googleColors.red, icon: <Layers className="w-8 h-8" /> },
        ],
      },
      process: {
        title: 'Notre Processus de Développement',
        subtitle: "De l'idée au lancement en 2-4 semaines",
        steps: [
          { num: '01', title: 'Briefing & Stratégie', desc: 'Nous comprenons vos objectifs et définissons la stratégie.', duration: '1-2 jours', icon: <FileText className="w-6 h-6" /> },
          { num: '02', title: 'Design UX/UI', desc: 'Nous créons wireframes et designs visuels.', duration: '3-5 jours', icon: <Palette className="w-6 h-6" /> },
          { num: '03', title: 'Développement', desc: 'Nous construisons votre site avec technologies modernes.', duration: '5-10 jours', icon: <Code className="w-6 h-6" /> },
          { num: '04', title: 'Lancement', desc: 'Nous publions et configurons le tracking.', duration: '1-2 jours', icon: <Zap className="w-6 h-6" /> },
        ],
      },
      included: {
        title: 'Que Comprend Notre Service ?',
        items: [
          { text: 'Design personnalisé', icon: <Palette className="w-5 h-5" /> },
          { text: 'Développement responsive', icon: <Smartphone className="w-5 h-5" /> },
          { text: 'Optimisation vitesse', icon: <Gauge className="w-5 h-5" /> },
          { text: 'SEO technique', icon: <Search className="w-5 h-5" /> },
          { text: 'Google Analytics 4', icon: <BarChart className="w-5 h-5" /> },
          { text: 'Google Tag Manager', icon: <Settings className="w-5 h-5" /> },
          { text: 'Formulaires optimisés', icon: <FileText className="w-5 h-5" /> },
          { text: 'Certificat SSL', icon: <CheckCircle className="w-5 h-5" /> },
          { text: '30 jours support', icon: <Clock className="w-5 h-5" /> },
          { text: 'Documentation', icon: <FileText className="w-5 h-5" /> },
        ],
      },
      stats: {
        items: [
          { value: '2-4', label: 'semaines livraison' },
          { value: '<3s', label: 'temps chargement' },
          { value: '100%', label: 'responsive' },
          { value: '30', label: 'jours support' },
        ],
      },
      portfolio: {
        title: 'Exemples de Notre Travail',
        subtitle: 'Sites web que nous avons créés pour nos clients',
        items: [
          { name: 'Arrancaya Barcelona', url: 'https://arrancaya-bcn.es/', desc: 'Service de démarrage de voitures à Barcelone', image: '/images/portfolio-arrancaya.png' },
          { name: 'Mon Certificat PEB', url: 'https://mon-certificat-peb.be/', desc: 'Certification énergétique en Belgique', image: '/images/portfolio-peb.png' },
          { name: 'Vivre Moins Cher', url: 'https://vivre-moins-cher.fr/', desc: 'Comparateur d\'offres en France', image: '/images/portfolio-vivre.png' },
        ],
      },
    },
    en: {
      hero: {
        badge: 'Conversion-Focused Web Development',
        title: 'Websites and Landing Pages that Convert',
        subtitle: 'We design and develop fast, modern websites optimized to convert visitors into customers.',
      },
      features: {
        title: 'Why Choose Us for Your Website?',
        subtitle: 'Web development focused on measurable results',
        items: [
          { icon: <Palette className="w-6 h-6" />, title: 'CRO Design', desc: 'Every element designed to maximize conversions.', color: googleColors.blue },
          { icon: <Gauge className="w-6 h-6" />, title: 'Optimized Speed', desc: 'Sites that load in under 3 seconds.', color: googleColors.green },
          { icon: <Smartphone className="w-6 h-6" />, title: 'Mobile-First', desc: 'Perfect responsive design on all devices.', color: googleColors.red },
          { icon: <BarChart className="w-6 h-6" />, title: 'Integrated Tracking', desc: 'Google Analytics, Tag Manager from day 1.', color: googleColors.yellow },
          { icon: <Search className="w-6 h-6" />, title: 'Technical SEO', desc: 'Structure optimized for search engines.', color: googleColors.blue },
          { icon: <Code className="w-6 h-6" />, title: 'Clean Code', desc: 'Development with modern technologies.', color: googleColors.green },
        ],
      },
      types: {
        title: 'Types of Web Projects',
        subtitle: 'Solutions adapted to each need',
        items: [
          { title: 'Landing Pages', desc: 'High-converting landing pages for Google Ads campaigns.', features: ['Persuasive design', 'Optimized forms', 'A/B testing', 'Speed <2s', 'Complete tracking', 'Mobile-first'], color: googleColors.blue, icon: <Target className="w-8 h-8" /> },
          { title: 'Corporate Sites', desc: 'Professional online presence with all necessary sections.', features: ['Multiple pages', 'Integrated blog', 'Easy CMS', 'SEO optimized', 'Contact forms', 'Responsive'], color: googleColors.green, icon: <Globe className="w-8 h-8" /> },
          { title: 'Microsites', desc: 'Specific sites for products or campaigns.', features: ['Product focus', 'Impactful design', 'CRM integration', 'Advanced analytics', 'Hosting included', 'Maintenance'], color: googleColors.red, icon: <Layers className="w-8 h-8" /> },
        ],
      },
      process: {
        title: 'Our Development Process',
        subtitle: 'From idea to launch in 2-4 weeks',
        steps: [
          { num: '01', title: 'Briefing & Strategy', desc: 'We understand your objectives and define strategy.', duration: '1-2 days', icon: <FileText className="w-6 h-6" /> },
          { num: '02', title: 'UX/UI Design', desc: 'We create wireframes and visual designs.', duration: '3-5 days', icon: <Palette className="w-6 h-6" /> },
          { num: '03', title: 'Development', desc: 'We build your site with modern technologies.', duration: '5-10 days', icon: <Code className="w-6 h-6" /> },
          { num: '04', title: 'Launch', desc: 'We publish and configure tracking.', duration: '1-2 days', icon: <Zap className="w-6 h-6" /> },
        ],
      },
      included: {
        title: 'What Does Our Service Include?',
        items: [
          { text: 'Custom design', icon: <Palette className="w-5 h-5" /> },
          { text: 'Responsive development', icon: <Smartphone className="w-5 h-5" /> },
          { text: 'Speed optimization', icon: <Gauge className="w-5 h-5" /> },
          { text: 'Technical SEO', icon: <Search className="w-5 h-5" /> },
          { text: 'Google Analytics 4', icon: <BarChart className="w-5 h-5" /> },
          { text: 'Google Tag Manager', icon: <Settings className="w-5 h-5" /> },
          { text: 'Optimized forms', icon: <FileText className="w-5 h-5" /> },
          { text: 'SSL Certificate', icon: <CheckCircle className="w-5 h-5" /> },
          { text: '30 days support', icon: <Clock className="w-5 h-5" /> },
          { text: 'Documentation', icon: <FileText className="w-5 h-5" /> },
        ],
      },
      stats: {
        items: [
          { value: '2-4', label: 'weeks delivery' },
          { value: '<3s', label: 'load time' },
          { value: '100%', label: 'responsive' },
          { value: '30', label: 'days support' },
        ],
      },
      portfolio: {
        title: 'Examples of Our Work',
        subtitle: 'Websites we have created for our clients',
        items: [
          { name: 'Arrancaya Barcelona', url: 'https://arrancaya-bcn.es/', desc: 'Car jump-start service in Barcelona', image: '/images/portfolio-arrancaya.png' },
          { name: 'Mon Certificat PEB', url: 'https://mon-certificat-peb.be/', desc: 'Energy certification in Belgium', image: '/images/portfolio-peb.png' },
          { name: 'Vivre Moins Cher', url: 'https://vivre-moins-cher.fr/', desc: 'Offer comparator in France', image: '/images/portfolio-vivre.png' },
        ],
      },
    },
  };

  const c = content[language] || content.es;

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <section className="py-16 lg:py-24 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${googleColors.blue} 0%, #1a73e8 100%)` }}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-white" />
          <div className="absolute bottom-20 right-20 w-48 h-48 rounded-full bg-white" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Globe className="w-4 h-4 text-white" />
                <span className="text-white">{c.hero.badge}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
                {c.hero.title}
              </h1>
              <p className="text-xl text-blue-100 leading-relaxed mb-8">
                {c.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={`/${language}/contacto`}>
                  <Button size="lg" className="bg-white hover:bg-slate-100 text-blue-600 px-8">
                    {t.hero.cta}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={openWhatsApp}
                  className="border-white/50 text-white hover:bg-white/10 px-8"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
            <div>
              <img src={images.webCreationIllustration} alt="Web Development" className="w-full h-auto rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {c.stats.items.map((stat, index) => {
              const colors = [googleColors.blue, googleColors.green, googleColors.red, googleColors.yellow];
              return (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold mb-1" style={{ color: colors[index] }}>{stat.value}</div>
                  <div className="text-slate-400 text-sm">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{c.features.title}</h2>
            <p className="text-lg text-slate-600">{c.features.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.features.items.map((item, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 bg-slate-50 rounded-2xl hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: item.color }}>
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{c.types.title}</h2>
            <p className="text-lg text-slate-600">{c.types.subtitle}</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {c.types.items.map((item, index) => (
              <Card key={index} className="bg-white border-0 shadow-xl hover:shadow-2xl transition-shadow">
                <div className="h-2" style={{ backgroundColor: item.color }} />
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white mb-6" style={{ backgroundColor: item.color }}>
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3">{item.title}</h3>
                  <p className="text-slate-600 mb-6">{item.desc}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {item.features.map((f, i) => (
                      <span key={i} className="flex items-center text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 mr-2" style={{ color: item.color }} />
                        {f}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{c.process.title}</h2>
            <p className="text-lg text-slate-600">{c.process.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {c.process.steps.map((step, index) => {
              const colors = [googleColors.blue, googleColors.red, googleColors.yellow, googleColors.green];
              return (
                <div key={index} className="relative">
                  {index < 3 && (
                    <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-slate-200" />
                  )}
                  <div className="text-center">
                    <div className="w-24 h-24 rounded-2xl flex flex-col items-center justify-center text-white mx-auto mb-4 shadow-lg" style={{ backgroundColor: colors[index] }}>
                      <span className="text-2xl font-bold">{step.num}</span>
                    </div>
                    <h3 className="font-bold text-lg text-slate-800 mb-2">{step.title}</h3>
                    <p className="text-slate-600 text-sm mb-2">{step.desc}</p>
                    <span className="text-xs font-medium px-3 py-1 rounded-full bg-slate-100 text-slate-600">{step.duration}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Included */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">{c.included.title}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {c.included.items.map((item, index) => {
              const colors = [googleColors.blue, googleColors.green, googleColors.red, googleColors.yellow];
              return (
                <div key={index} className="flex items-center space-x-3 p-4 bg-white rounded-xl shadow-sm">
                  <div className="text-white w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colors[index % 4] }}>
                    {item.icon}
                  </div>
                  <span className="text-slate-700 font-medium">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio */}
      {c.portfolio && (
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">{c.portfolio.title}</h2>
              <p className="text-lg text-slate-600">{c.portfolio.subtitle}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {c.portfolio.items.map((item, index) => {
                const colors = [googleColors.blue, googleColors.green, googleColors.red];
                return (
                  <a
                    key={index}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-slate-100"
                  >
                    <div className="h-52 relative overflow-hidden bg-slate-100">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-4 right-4 bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                        <ExternalLink className="w-5 h-5" style={{ color: colors[index % 3] }} />
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {item.name}
                      </h3>
                      <p className="text-slate-600 text-sm mb-3">{item.desc}</p>
                      <span className="text-sm font-medium flex items-center" style={{ color: colors[index % 3] }}>
                        {item.url.replace('https://', '').replace('/', '')}
                        <ExternalLink className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center space-x-2 mb-6">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.blue }} />
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.red }} />
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.yellow }} />
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.green }} />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t.cta.title}
          </h2>
          <p className="text-slate-400 text-lg mb-8">{t.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/contacto`}>
              <Button size="lg" className="text-white px-8" style={{ backgroundColor: googleColors.blue }}>
                {t.cta.button}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button
              variant="outline"
              size="lg"
              onClick={openWhatsApp}
              className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 px-8"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebCreationPage;
